import { NavLink, Outlet, useNavigate, useLocation } from "react-router-dom";
import { ErrorBoundary } from "@/components/ErrorBoundary";
import { LayoutDashboard, ShoppingCart, Boxes, Scissors, Warehouse, ArrowLeftRight, Users, LogOut, Menu, BarChart3, AlertTriangle, Settings, ScrollText, KeyRound, Contact, Calculator, BookOpen, Receipt } from "lucide-react";
import { useState } from "react";
import { useAuth } from "@/hooks/useAuth";
import { ROLE_LABEL, can, logoUrl } from "@/lib/api";
import { useSettings } from "@/hooks/useSettings";
import { Button } from "@/components/ui/button";
import { ChangePasswordDialog } from "@/components/ChangePasswordDialog";
import { VersionBanner } from "@/components/VersionBanner";
import { TransferAlertBanner, useTransferAlerts } from "@/components/TransferAlerts";
import { NotificationsBell } from "@/components/Notifications";

const NAV = [
  { to: "/", label: "Tableau de bord", icon: LayoutDashboard, roles: ["admin", "gestionnaire", "vendeur"] },
  { to: "/ventes", label: "Vente", icon: ShoppingCart, roles: ["admin", "gestionnaire", "vendeur"], perm: "caisse" },
  { to: "/stock", label: "Document des Stocks", icon: Boxes, roles: ["admin", "gestionnaire", "vendeur"], perm: "stock" },
  { to: "/achats", label: "Achat", icon: Receipt, roles: ["admin", "gestionnaire", "vendeur"], color: "bg-[#059669]", perm: "fournisseurs" },
  { to: "/tissus", label: "Liste des Tissus", icon: Scissors, roles: ["admin", "gestionnaire", "vendeur"], perm: "tissus" },
  { to: "/transferts", label: "Transferts", icon: ArrowLeftRight, roles: ["admin", "gestionnaire", "vendeur"], color: "bg-blue-600", perm: "transferts" },
  { to: "/rapports", label: "Rapports", icon: BarChart3, roles: ["admin", "gestionnaire", "vendeur"], color: "bg-emerald-600", perm: "rapports" },
  { to: "/alertes", label: "Alertes stock", icon: AlertTriangle, roles: ["admin", "gestionnaire", "vendeur"], perm: "alertes" },
  { to: "/depots", label: "Dépôts", icon: Warehouse, roles: ["admin"] },
  { to: "/clients", label: "Clients", icon: Contact, roles: ["admin", "gestionnaire", "vendeur"], perm: "clients" },
  { to: "/comptabilite", label: "Comptabilité", icon: Calculator, roles: ["admin", "gestionnaire", "vendeur"], perm: "clients" },
  { to: "/journal", label: "Journal", icon: ScrollText, roles: ["admin", "gestionnaire", "vendeur"], perm: "stock", action: "supprimer" },
  { to: "/utilisateurs", label: "Utilisateurs", icon: Users, roles: ["admin"] },
  { to: "/parametres", label: "Paramètres", icon: Settings, roles: ["admin"] },
  { to: "/manuel", label: "Manuel", icon: BookOpen, roles: ["admin", "gestionnaire", "vendeur"] },
];

export default function Layout() {
  const { user, logout } = useAuth();
  const { settings } = useSettings();
  const nav = useNavigate();
  const location = useLocation();
  const [open, setOpen] = useState(false);
  const [pwd, setPwd] = useState(false);
  const alerts = useTransferAlerts();

  const Sidebar = () => (
    <aside className="sidebar flex h-full w-64 flex-col overflow-y-auto p-5 text-foreground">
      <div className="mb-8 flex items-center gap-3">
        <div className="flex h-10 w-10 items-center justify-center overflow-hidden rounded-md bg-white ring-1 ring-border">{settings.logo_v ? <img src={logoUrl(settings.logo_v)} alt="logo" className="h-full w-full object-contain" data-testid="sidebar-logo" /> : <Scissors size={20} className="text-[#059669]" />}</div>
        <div><p className="font-display text-xl font-semibold leading-none" data-testid="company-name">{settings.company_name}</p><p className="text-xs text-muted-foreground">Gestion multi-dépôts</p></div>
      </div>
      <nav className="flex-1 space-y-1">
        {NAV.filter((n) => n.roles.includes(user.role) && (!n.perm || can(user, n.perm, n.action || "voir"))).map((n) => (
          <NavLink key={n.to} to={n.to} end={n.to === "/"} onClick={() => setOpen(false)} data-testid={`nav-${n.to.replace("/", "") || "dashboard"}`}
            className={({ isActive }) => `sidebar-link ${isActive ? `active ${n.color || ""}` : ""}`}>
            <n.icon size={18} /> {n.label}{n.to === "/transferts" && alerts.length > 0 && <span className="ml-auto rounded-full bg-red-600 px-2 py-0.5 text-[11px] font-bold text-white" data-testid="nav-transfers-badge">{alerts.length}</span>}
          </NavLink>
        ))}
      </nav>
      <div className="border-t pt-4">
        <p className="mb-3 text-[11px] text-muted-foreground" data-testid="copyright">{settings.footer}{settings.phone && <span className="block">Tél : {settings.phone}</span>}</p>
        <p className="truncate text-sm font-semibold" data-testid="current-user-name">{user.name}</p>
        <p className="text-xs text-[#059669]">{ROLE_LABEL[user.role] || user.role}</p>
        <Button variant="ghost" size="sm" className="mt-3 w-full justify-start text-slate-600" onClick={() => setPwd(true)} data-testid="change-password-btn"><KeyRound size={16} /> Mon mot de passe</Button>
        <Button variant="ghost" size="sm" className="w-full justify-start text-slate-600" data-testid="logout-btn"
          onClick={() => { logout(); nav("/login"); }}><LogOut size={16} /> Déconnexion</Button>
      </div>
    </aside>
  );

  return (
    <div className="flex h-screen overflow-hidden">
      <div className="hidden lg:block"><Sidebar /></div>
      {open && <div className="fixed inset-0 z-40 flex lg:hidden"><div className="z-50 shadow-xl"><Sidebar /></div><div className="flex-1 bg-black/50" onClick={() => setOpen(false)} /></div>}
      <ChangePasswordDialog open={pwd} onClose={() => setPwd(false)} />
      <main className="grain flex-1 overflow-y-auto">
        <VersionBanner />
        <TransferAlertBanner alerts={alerts} />
        <div className="flex items-center gap-3 border-b bg-card/70 px-4 py-3 lg:hidden">
          <Button variant="ghost" size="icon" onClick={() => setOpen(true)} data-testid="mobile-menu-btn"><Menu /></Button>
          <span className="flex-1 font-display text-lg font-semibold">{settings.company_name}</span>
          <NotificationsBell />
        </div>
        <div className="hidden justify-end px-4 pt-3 lg:flex"><NotificationsBell /></div>
        <div className="mx-auto max-w-7xl p-4 sm:p-6 lg:p-8"><ErrorBoundary location={location.pathname}><Outlet /></ErrorBoundary></div>
      </main>
    </div>
  );
}
